import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Typography, useMediaQuery, useTheme, Chip, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid2';
import FullHeightCard from '@ui/pages/Home/components/FullHeightCard';
import { MagicStar } from 'iconsax-react';
import SponsorshipProfile from './components';
import SponsorshipCard from './components/SponsorshipCard';
import UpcomingEventCard from './components/UpcomingEventCard';
import NoDataMessage from '@ui/components/common/NoDataMessage';
import { useSelector } from 'react-redux';
import { PartnerStatusEnum } from '@application/enums/partner-status-enum';
import { useEffect } from 'react';
import { useAppDispatch } from '@application/store/useStore';
import { useParams } from 'react-router-dom';
import { getPartnerByIdAsync } from '@application/features/partner/partnerThunk';
const DetailsTab = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { partner } = useSelector((state) => state.partner);
    const handleEventClick = (eventId) => {
        console.log('Event clicked:', eventId);
    };
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const { id } = useParams();
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (id) {
            dispatch(getPartnerByIdAsync(id));
        }
    }, [dispatch, id]);
    const primaryContact = partner?.contactPersons?.find((c) => c?.isPrimary) ?? partner?.contactPersons?.[0];
    const sponsorshipData = {
        id: partner?.id ?? '',
        partnerName: partner?.fullName?.trim() ||
            partner?.name?.trim() ||
            `${partner?.firstName ?? ''} ${partner?.lastName ?? ''}`.trim() ||
            '',
        partnerLogo: partner?.logoUrl,
        status: partner?.status === PartnerStatusEnum.Active ? 'Active' : 'Inactive',
        sponsorshipType: '',
        contactPerson: {
            name: primaryContact
                ? `${primaryContact.firstName ?? ''} ${primaryContact.lastName ?? ''}`.trim()
                : '',
            email: primaryContact?.email,
            phone: primaryContact?.phoneNo,
            avatar: primaryContact?.profilePictureUrl
        },
        benefitsUtilization: 0
    };
    const formattedUpcomingEventRange = (() => {
        const startDate = partner?.upcomingEventStartDate
            ? new Date(partner.upcomingEventStartDate)
            : null;
        const endDate = partner?.upcomingEventEndDate ? new Date(partner.upcomingEventEndDate) : null;
        const formatDate = (d) => d && !isNaN(d.getTime()) ? `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}` : '';
        const start = formatDate(startDate);
        const end = formatDate(endDate);
        const startTime = partner?.upcomingEventStartTime || '';
        const endTime = partner?.upcomingEventEndTime || '';
        if (!start && !end && !startTime && !endTime)
            return '';
        if (start && end) {
            const times = [startTime, endTime].filter(Boolean).join(' → ');
            return times ? `${start} → ${end} • ${times}` : `${start} → ${end}`;
        }
        if (start) {
            return [start, startTime].filter(Boolean).join(' • ');
        }
        return [end, endTime].filter(Boolean).join(' • ');
    })();
    const upcomingEvents = (partner?.upcomingEvents ?? []).map((e, idx) => {
        const d = e.startDate ? new Date(e.startDate) : null;
        const date = d && !isNaN(d.getTime())
            ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : '';
        const details = `${e.ticketsUploadedCount} tickets, ${e.interestsCount} tags, ${e.assignedUsersCount} assigned`;
        return {
            id: String(idx),
            title: e.title,
            date,
            location: e.location || '',
            details
        };
    });
    // Tags are rendered from partner.interestTitles below
    return (_jsx(Box, { children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { size: { xs: 12, sm: 8 }, sx: {
                        height: isSmallScreen ? undefined : 'calc(100dvh - 121px)',
                        overflowY: 'auto',
                        pb: isSmallScreen ? 0 : 1
                    }, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: '12px', children: [_jsx(SponsorshipProfile, { sponsorship: sponsorshipData }), _jsx(SponsorshipCard, { variant: 'gradient', dualHeader: true, disableShadow: true, heading: t('sponsorships.upcomingEvent'), secondaryHeading: t('sponsorships.schedule'), secondaryContent: _jsx(Box, { children: _jsx(Typography, { variant: 'body1', color: 'inherit', children: formattedUpcomingEventRange || '-' }) }), children: _jsx(Typography, { variant: 'body1', color: 'inherit', fontWeight: 400, children: partner?.upcomingEventTitle || '-' }) }), _jsx(SponsorshipCard, { variant: 'default', dualHeader: false, disableShadow: true, heading: t('sponsorships.sponsorshipDescription'), children: partner?.description ? (_jsx(Typography, { variant: 'body1', color: 'text.primary', children: partner.description })) : (_jsx(NoDataMessage, { text: t('common.noDataAvailable', {
                                        data: t('sponsorships.sponsorshipDescription')
                                    }) })) }), _jsx(SponsorshipCard, { variant: 'default', disableShadow: true, dualHeader: false, heading: 'Tags', children: partner?.interestTitles && partner.interestTitles.length > 0 ? (_jsx(Box, { display: 'flex', flexWrap: 'wrap', gap: 1, children: partner.interestTitles.map((title) => (_jsx(Chip, { label: title.replace(/,\s*$/, ''), size: 'medium', sx: {
                                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                                            color: (theme) => theme.palette.primary.dark,
                                            borderRadius: '8px',
                                            fontWeight: 500
                                        } }, title))) })) : (_jsx(NoDataMessage, { text: t('common.noDataAvailable', { data: 'Tags' }) })) })] }) }), _jsx(Grid, { size: { xs: 12, sm: 4 }, children: _jsx(Box, { sx: { height: 'calc(100vh - 135px)' }, children: _jsx(FullHeightCard, { heading: t('sponsorships.upcomingEvents'), icon: _jsx(MagicStar, { variant: 'Outline' }), filter: false, disableShadow: true, disableBackground: true, disableBorders: true, headerTypographyVariant: 'h4', headerTypographyColor: theme.palette.text.primary, cardHeaderPadding: '0px 0px 12px 4px', cardContentContainersx: { padding: 0, height: 'calc(100dvh - 162px)' }, children: _jsx(Box, { sx: {
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.75,
                                    height: '100%',
                                    overflow: 'hidden'
                                }, children: upcomingEvents && upcomingEvents.length > 0 ? (_jsx(Box, { sx: {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.75,
                                        overflow: 'auto',
                                        flex: 1,
                                        paddingRight: 0.5,
                                        pb: theme.spacing(2.5)
                                    }, children: upcomingEvents.map((event) => (_jsx(UpcomingEventCard, { title: event.title, date: event.date, location: event.location, details: event.details, onClick: () => handleEventClick(event.id) }, event.id))) })) : (_jsx(NoDataMessage, { text: t('common.noDataAvailable', { data: t('sponsorships.upcomingEvents') }) })) }) }) }) })] }) }));
};
export default DetailsTab;
